import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ArrowLeft, AlertCircle, Download } from "lucide-react";
import HighlightedText from "../components/HighlightedText";
import Header from "../components/Header";
import { useAuth } from "../lib/AuthContext";

const ReportPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null);
  const [report, setReport] = useState(null);
  const { token, isReady } = useAuth();

  const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (!token) {
      setError("Vui lòng đăng nhập để xem báo cáo.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/history/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Tải báo cáo thất bại");
        setHistory(data.history);
        setReport(data.report);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token, isReady, API_BASE]);

  // Normalize from stored report to display like ResultsPage
  const normalized = report
    ? {
        type:
          report.fullResponse?.type ||
          (typeof report.fullResponse?.combined_prob_ai === "number" ? "ai_check" : "plagiarism_check"),
        label: report.fullResponse?.label ?? null,
        result_text: report.fullResponse?.result_text ?? "",
        is_plagiarism: report.isPlagiarism,
        confidence: report.confidence,
        threshold: report.threshold,
        best_match: report.bestMatch,
        top_matches: report.topMatches || [],
        sentence_analysis: report.sentenceAnalysis || [],
        stats: report.stats || {},
        combined_prob_ai: report.fullResponse?.combined_prob_ai ?? report.confidence ?? 0,
        combined_label: report.fullResponse?.combined_label || "Nguoi viet",
      }
    : null;

  const isAIReport = normalized?.type === "ai_check";
  const isLegacyHighConfidenceReport =
    !isAIReport &&
    !normalized?.is_plagiarism &&
    Number(normalized?.confidence ?? 0) >= 0.7;
  const isParaphraseReport =
    normalized?.label === 4 ||
    normalized?.result_text === "Diễn đạt lại";
  const totalSegments = normalized?.sentence_analysis?.length || 0;
  const suspiciousSegments = normalized?.sentence_analysis?.filter((sent) => sent.confidence >= 0.6).length || 0;
  const humanSegments = Math.max(totalSegments - suspiciousSegments, 0);
  const aiSegmentRatio = totalSegments > 0 ? (suspiciousSegments / totalSegments) * 100 : 0;
  const avgSegmentConfidence = totalSegments > 0
    ? normalized.sentence_analysis.reduce((sum, sent) => sum + Number(sent.confidence || 0), 0) / totalSegments
    : Number(normalized?.stats?.avg_confidence ?? 0);

  const getAISummary = (score) => {
    if (score >= 0.8) {
      return "Rất cao - có thể là AI viết";
    }
    if (score >= 0.6) {
      return "Cao - có dấu hiệu AI rõ ràng";
    }
    if (score >= 0.4) {
      return "Trung bình - nội dung đáng nghi";
    }
    return "Thấp - nghiêng về người viết";
  };

  const handleExportPdf = () => {
    window.print();
  };

  const getPlagiarismStatus = () => {
    if (isParaphraseReport) {
      return {
        label: "CÓ DẤU HIỆU DIỄN ĐẠT LẠI",
        color: "text-amber-700",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200",
      };
    }

    if (normalized?.is_plagiarism || isLegacyHighConfidenceReport) {
      return {
        label: "CÓ DẤU HIỆU ĐẠO VĂN",
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
      };
    }

    if (!normalized?.is_plagiarism) {
      return {
        label: "KHÔNG ĐẠO VĂN",
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
      };
    }

    return {
      label: "CÓ DẤU HIỆU ĐẠO VĂN",
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
    };
  };

  const plagiarismStatus = getPlagiarismStatus();

  return (
    <div className="app-shell">
      <div className="print-hidden">
        <Header />
      </div>

      <main className="page-main">
      <div className="max-w-6xl mx-auto px-4 py-8 print-report-container">
        <div className="print-report">
        <div className="mb-6 print-hidden">
          <Link to="/history">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Quay lại Lịch sử
            </Button>
          </Link>
        </div>

        {error && (
          <Card className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-500" />
            <div className="text-red-600 font-medium mb-2">{error}</div>
            <div className="text-sm text-muted-foreground">Hãy đảm bảo bạn đã đăng nhập.</div>
          </Card>
        )}

        {loading && <Card className="p-8">Đang tải báo cáo...</Card>}

        {!loading && !error && history && normalized && (
          <>
            <div className="mb-4">
              <h1 className="text-3xl font-bold">Chi tiết báo cáo</h1>
              <p className="mt-2 text-base font-medium text-foreground">
                {history.title || "Chưa đặt tên"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {history.wordCount} từ • {new Date(history.createdAt).toLocaleString()}
              </p>
            </div>

            {!isAIReport ? (
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className={`p-6 print-card flex items-center justify-center ${plagiarismStatus.bgColor} border-2 ${plagiarismStatus.borderColor}`}>
                      <div className="text-center">
                    <div className="text-sm text-muted-foreground">Trạng thái</div>
                    <div className={`text-3xl font-bold ${plagiarismStatus.color} mb-2`}>
                      {plagiarismStatus.label}
                    </div>
                  </div>
                </Card>

                <Card className="p-6 print-card">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">Điểm Confidence</h3>
                    <span className={`text-3xl font-bold ${normalized.confidence >= 0.6 ? 'text-red-600' : 'text-green-600'}`}>
                      {(normalized.confidence * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-3 w-full bg-gray-200 rounded overflow-hidden">
                    <div className={`h-full ${normalized.confidence >= 0.6 ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${normalized.confidence * 100}%` }} />
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Ngưỡng: {(normalized.threshold * 100).toFixed(0)}%
                  </p>
                </Card>

              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <Card className={`p-6 print-card ${normalized.combined_prob_ai >= 0.6 ? 'bg-red-50 border-2 border-red-200' : 'bg-green-50 border-2 border-green-200'}`}>
                    <div className="text-center">
                      <div className={`text-3xl font-bold ${normalized.combined_prob_ai >= 0.6 ? 'text-red-600' : 'text-green-600'} mb-2`}>
                        {getAISummary(normalized.combined_prob_ai)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Nhãn phát hiện AI: {normalized.combined_label}
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6 print-card">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold">Điểm AI toàn văn bản</h3>
                      <span className={`text-3xl font-bold ${normalized.combined_prob_ai >= 0.6 ? 'text-red-600' : 'text-green-600'}`}>
                        {(normalized.combined_prob_ai * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-3 w-full bg-gray-200 rounded overflow-hidden">
                      <div
                        className={`h-full ${normalized.combined_prob_ai >= 0.6 ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: `${normalized.combined_prob_ai * 100}%` }}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      Ngưỡng AI: {(normalized.threshold * 100).toFixed(0)}%
                    </p>
                  </Card>
                </div>

                <Card className="p-6 mb-8 print-card">
                  <h2 className="text-2xl font-bold mb-4">Thống kê phân tích văn bản</h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
                    <div>
                      <div className="text-4xl font-bold text-blue-600">{totalSegments}</div>
                      <div className="text-sm text-muted-foreground">Tổng số đoạn</div>
                    </div>
                    <div>
                      <div className="text-4xl font-bold text-red-600">{suspiciousSegments}</div>
                      <div className="text-sm text-muted-foreground">Đoạn nghi AI</div>
                    </div>
                    <div>
                      <div className="text-4xl font-bold text-green-600">{humanSegments}</div>
                      <div className="text-sm text-muted-foreground">Đoạn do người viết</div>
                    </div>
                    <div>
                      <div className="text-4xl font-bold text-orange-500">{aiSegmentRatio.toFixed(1)}%</div>
                      <div className="text-sm text-muted-foreground">Tỷ lệ đoạn nghi AI</div>
                    </div>
                    <div>
                      <div className="text-4xl font-bold text-violet-600">{(avgSegmentConfidence * 100).toFixed(1)}%</div>
                      <div className="text-sm text-muted-foreground">Điểm trung bình các đoạn</div>
                    </div>
                  </div>
                  <div className="mt-6 text-sm text-muted-foreground">
                    Số từ đã phân tích: <span className="font-semibold text-foreground">{history.wordCount}</span>
                  </div>
                </Card>
              </>
            )}

            {/*
            Future restore option: plagiarism best-match score card.
            <Card className="p-6 print-card">
              <div className="text-sm text-muted-foreground">Điểm nguồn tương đồng tốt nhất</div>
              <div className="text-3xl font-bold text-orange-600">
                {normalized.best_match?.final_score ? (normalized.best_match.final_score * 100).toFixed(1) : '0.0'}%
              </div>
              {normalized.best_match?.title && (
                <div className="mt-2 text-sm">{normalized.best_match.title}</div>
              )}
            </Card>
            */}

            {/* Top matches */}
            {!isAIReport && normalized.top_matches && normalized.top_matches.length > 0 && (
              <Card className="p-6 mb-8 print-card">
                <h2 className="text-2xl font-bold mb-4">Top Nguồn Tương Đồng</h2>
                <div className="space-y-3">
                  {normalized.top_matches.map((m, i) => (
                    <div key={i} className="border-l-4 border-orange-500 pl-4 py-2 bg-orange-50 rounded">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">#{i + 1} - {m.title || m.doc_id}</span>
                        <span className="text-sm text-orange-600 font-medium">{(m.score * 100).toFixed(1)}%</span>
                      </div>
                      {m.url && (
                        <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                          {m.url}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Analyzed Text */}
            <Card className="p-6 print-card">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
                <h2 className="text-2xl font-bold">
                  {isAIReport ? "Văn bản được phân tích" : "Văn Bản Đã Phân Tích"}
                </h2>
                <div className="print-hidden">
                  <Button className="report-export-button" onClick={handleExportPdf}>
                    <Download className="w-4 h-4 mr-2" />
                    Xuất Báo Cáo PDF
                  </Button>
                </div>
              </div>
              {normalized.sentence_analysis && normalized.sentence_analysis.length > 0 ? (
                <HighlightedText sentenceAnalysis={normalized.sentence_analysis} />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-muted-foreground">{history.text}</pre>
              )}
            </Card>
          </>
        )}
      </div>
      </div>
      </main>
    </div>
  );
};

export default ReportPage;
